import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Plus, Edit2, Trash2, Save, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_COLORS = ["#0057D9", "#0EA5E9", "#8B5CF6", "#F59E0B", "#EF4444", "#EC4899", "#14B8A6", "#64748B"];

const BlogCategoriesList = () => {
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null); // {id?, name, slug, description, color}
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/cms/blog-categories`);
      setCats(r.data);
    } catch {
      toast.error("Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing?.name) return toast.error("Name is required");
    try {
      if (editing.id) {
        await axios.put(`${API}/cms/blog-categories/${editing.id}`, editing);
        toast.success("Updated");
      } else {
        await axios.post(`${API}/cms/blog-categories`, editing);
        toast.success("Created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this category? Posts assigned to it will lose the tag.")) return;
    await axios.delete(`${API}/cms/blog-categories/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/app/website/blog" className="text-slate-500 hover:text-slate-900 p-1"><ArrowLeft size={18} /></Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Blog Categories</h1>
            <p className="text-sm text-slate-500">Taxonomies shown at /blog/category/:slug</p>
          </div>
        </div>
        <button onClick={() => setEditing({ name: "", slug: "", description: "", color: "#0057D9" })} className="inline-flex items-center gap-2 bg-prepify-600 hover:bg-prepify-700 text-white font-medium px-4 py-2 rounded-lg text-sm" data-testid="new-category-btn">
          <Plus size={14} /> New category
        </button>
      </header>

      {loading ? (
        <div className="text-slate-400">Loading…</div>
      ) : cats.length === 0 ? (
        <div className="text-slate-400 text-center py-16 border-2 border-dashed border-slate-200 rounded-xl">
          No categories yet. Create your first one.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold">Slug</th>
                <th className="text-left px-4 py-3 font-semibold">Color</th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {cats.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`category-row-${c.slug}`}>
                  <td className="px-4 py-3">
                    <div className="inline-flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full" style={{ background: c.color || "#0057D9" }} />
                      <b className="text-slate-900">{c.name}</b>
                    </div>
                    {c.description && <div className="text-xs text-slate-500 mt-0.5">{c.description}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-slate-500">/{c.slug}</td>
                  <td className="px-4 py-3"><code className="text-xs bg-slate-100 px-2 py-0.5 rounded">{c.color}</code></td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => setEditing(c)} className="text-xs px-2.5 py-1 border border-slate-200 rounded hover:bg-slate-50 inline-flex items-center gap-1" data-testid={`edit-cat-${c.slug}`}><Edit2 size={12} /> Edit</button>
                      <button onClick={() => remove(c.id)} className="text-xs px-2.5 py-1 border border-slate-200 rounded hover:bg-red-50 hover:text-red-600 hover:border-red-200" data-testid={`delete-cat-${c.slug}`}><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-900">{editing.id ? "Edit category" : "New category"}</h3>
              <button onClick={() => setEditing(null)} className="p-1 hover:bg-slate-100 rounded"><X size={16} /></button>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Name</label>
              <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-prepify-500/40" data-testid="category-name-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Slug <span className="text-xs text-slate-400">(auto if blank)</span></label>
              <input value={editing.slug || ""} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-prepify-500/40" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Description</label>
              <textarea rows={2} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-prepify-500/40" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Color</label>
              <div className="mt-1.5 flex flex-wrap gap-2">
                {DEFAULT_COLORS.map((c) => (
                  <button key={c} onClick={() => setEditing({ ...editing, color: c })} className={`w-8 h-8 rounded-full border-2 transition ${editing.color === c ? "border-slate-900 scale-110" : "border-transparent"}`} style={{ background: c }} />
                ))}
                <input type="color" value={editing.color || "#0057D9"} onChange={(e) => setEditing({ ...editing, color: e.target.value })} className="w-8 h-8 rounded-full cursor-pointer" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
              <button onClick={save} className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-slate-900 hover:bg-prepify-600 text-white rounded-lg font-medium" data-testid="save-category-btn"><Save size={14} /> Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogCategoriesList;
